import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';

const csvTransformKey = () => orpc.rebates.csvTransform.getSession.key();

export function useCreateRebateCsvTransformSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      fileName: string;
      contentType?: string;
      fileBase64: string;
      fileNames?: string[];
      sourceContext?: Record<string, unknown>;
      selectedSheet?: string;
    }) => rpcClient.rebates.csvTransform.createSession(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: csvTransformKey() });
    },
  });
}

export function useSelectRebateCsvTransformSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; sheetName: string }) =>
      rpcClient.rebates.csvTransform.selectSheet(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: csvTransformKey() });
    },
  });
}

export function useSaveRebateCsvTransformSnapshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      stepLabel: string;
      csvContent: string;
      state?: Record<string, unknown>;
      rowCountOutput?: number;
    }) => rpcClient.rebates.csvTransform.saveSnapshot(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.csvTransform.listSnapshots.key(),
      });
    },
  });
}

export function useListRebateCsvTransformSessions(
  limit = 25,
  filters?: { fromDate?: string; toDate?: string },
) {
  return useQuery({
    ...orpc.rebates.csvTransform.listSessions.queryOptions({
      input: { limit, ...filters },
    }),
  });
}

export function useRebateCsvTransformSession(
  sessionId: string | null,
  enabled = true,
) {
  return useQuery({
    ...orpc.rebates.csvTransform.getSession.queryOptions({
      input: { sessionId: sessionId ?? '' },
    }),
    enabled: enabled && !!sessionId,
  });
}

export function useRebateCsvTransformSnapshots(
  sessionId: string | null,
  enabled = true,
) {
  return useQuery({
    ...orpc.rebates.csvTransform.listSnapshots.queryOptions({
      input: { sessionId: sessionId ?? '' },
    }),
    enabled: enabled && !!sessionId,
  });
}

export function useGetRebateCsvTransformArtifact() {
  return useMutation({
    mutationFn: (input: { sessionId: string; artifactKey: string }) =>
      rpcClient.rebates.csvTransform.getArtifact(input),
  });
}

export function useStartRebateCsvTransform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId?: string;
      inputArtifactKey?: string;
      documentId?: string;
      instructions?: string;
      fileNames?: string[];
      sourceContext?: Record<string, unknown>;
    }) => rpcClient.rebates.csvTransform.start(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: csvTransformKey() });
    },
  });
}

export function useRewindRebateCsvTransformStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      stepLabel: string;
      inputOverride?: Record<string, unknown>;
    }) => rpcClient.rebates.csvTransform.rewindStep(input),
    // NOTE: param _variables currently unused (kept via '_' prefix) — revisit and delete if not needed
    onSuccess: (_, _variables) => {
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.csvTransform.getSession.key(),
      });
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.csvTransform.listSnapshots.key(),
      });
    },
  });
}

export function useResumeRebateCsvTransformAutoRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string }) =>
      rpcClient.rebates.csvTransform.resumeAutoRun(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: csvTransformKey() });
    },
  });
}

export function useCompleteRebateCsvTransformSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string }) =>
      rpcClient.rebates.csvTransform.completeSession(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.csvTransform.listSessions.key(),
      });
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.csvTransform.getSession.key(),
      });
    },
  });
}

export function useCheckDuplicateRebateCsvTransformSessions(
  fileName: string | null,
  enabled = true,
) {
  return useQuery({
    ...orpc.rebates.csvTransform.checkDuplicateSessions.queryOptions({
      input: { fileName: fileName ?? '' },
    }),
    enabled: enabled && !!fileName,
  });
}

export function useDeleteRebateCsvTransformSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string }) =>
      rpcClient.rebates.csvTransform.deleteSession(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.csvTransform.listSessions.key(),
      });
    },
  });
}

export function useGetRebateSenderContactOffer() {
  const queryClient = useQueryClient();
  return (input: { sessionId: string; supplierId: string }) =>
    queryClient.fetchQuery(
      orpc.rebates.csvTransform.getSenderContactOffer.queryOptions({
        input,
      }),
    );
}

export function useSaveRebateSenderContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; supplierId: string }) =>
      rpcClient.rebates.csvTransform.saveSenderContact(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsPayable.supplierContacts.list.key(),
      });
    },
  });
}
